// CustomerTimePicker.jsx
import React, { useEffect, useCallback, useState } from "react";
import { Box, Grid, Typography, Stack, TextField, MenuItem } from "@mui/material";
import dayjs from "dayjs";

const formatTimePart = (value) => value.toString().padStart(2, "0");

const hoursOptions = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const minutesOptions = Array.from({ length: 60 }, (_, i) => formatTimePart(i)); // "00".."59"
const ampmOptions = ["AM", "PM"];

const CustomerTimePicker = ({
  tempSelectedDays,
  daySlot,
  selectedPlan,
  dayTimeState,
  setDayTimeState,
  startDate,
}) => {
  const [validationErrors, setValidationErrors] = useState({});

  // convert 12h -> 24h
  const convertTo24Hour = useCallback((hour12, period) => {
    const h = parseInt(hour12, 10);
    if (Number.isNaN(h)) return NaN;
    if (period === "PM" && h !== 12) return h + 12;
    if (period === "AM" && h === 12) return 0;
    return h;
  }, []);

  // Initialize defaults per your rules:
  
  // useEffect(() => {
  //   if (!Array.isArray(tempSelectedDays) || tempSelectedDays.length === 0) return;

  //   setDayTimeState((prev) => {
  //     const next = { ...prev };
  //     const todayStr = dayjs().format("YYYY-MM-DD");
  //     const startStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
  //     const todayName = dayjs().format("dddd").toLowerCase();
  //     let changed = false;

  //     tempSelectedDays.forEach((dayIndex) => {
  //       const dayName = daySlot[dayIndex + 1]; // your mapping
  //       const isTodaySelected =
  //         startStr === todayStr && dayName && dayName.toLowerCase() === todayName;

  //       const defaultPickup = isTodaySelected ? dayjs().format("HH:mm") : "00:00";

  //       if (!next[dayName]) {
  //         // brand new entry -> set default
  //         next[dayName] = {
  //           pickup: defaultPickup,
  //           ...(selectedPlan?.tripType === 2 ? { drop: "00:00" } : {}),
  //         };
  //         changed = true;
  //       } else {
  //         // existing entry -> ensure pickup exists; if placeholder "00:00" and is today, override with now
  //         const existingPickup = next[dayName].pickup;
  //         if (!existingPickup || existingPickup === "") {
  //           next[dayName].pickup = defaultPickup;
  //           changed = true;
  //         } else if (isTodaySelected && existingPickup === "00:00") {
  //           // override placeholder midnight with current time for today
  //           next[dayName].pickup = defaultPickup;
  //           changed = true;
  //         }

  //         // ensure drop exists for two-way
  //         if (selectedPlan?.tripType === 2 && (next[dayName].drop === undefined || next[dayName].drop === "")) {
  //           next[dayName].drop = "00:00";
  //           changed = true;
  //         }
  //       }
  //     });

  //     if (changed) {
  //       sessionStorage.setItem("dayTimeState", JSON.stringify(next));
  //       return next;
  //     }
  //     return prev;
  //   });
  // }, [tempSelectedDays, startDate, selectedPlan, daySlot, setDayTimeState]);

  useEffect(() => {
  if (!Array.isArray(tempSelectedDays) || tempSelectedDays.length === 0) return;

  setDayTimeState((prev) => {
    const next = { ...prev };
    const todayStr = dayjs().format("YYYY-MM-DD");
    const startStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
    const todayName = dayjs().format("dddd").toLowerCase();
    let changed = false;

    tempSelectedDays.forEach((dayIndex) => {
      const dayName = daySlot[dayIndex + 1]; 
      const isTodaySelected =
        startStr === todayStr && dayName && dayName.toLowerCase() === todayName;

      const nowTime = dayjs().format("HH:mm");
      const defaultPickup = isTodaySelected ? nowTime : "00:00";

      if (!next[dayName]) {
        // brand new entry -> set default
        next[dayName] = {
          pickup: defaultPickup,
          ...(selectedPlan?.tripType === 2 ? { drop: defaultPickup } : {}), // drop same as pickup if today
        };
        changed = true;
      } else {
        // existing entry -> ensure pickup exists
        const existingPickup = next[dayName].pickup;
        if (!existingPickup || existingPickup === "") {
          next[dayName].pickup = defaultPickup;
          changed = true;
        } else if (isTodaySelected && existingPickup === "00:00") {
          next[dayName].pickup = defaultPickup;
          changed = true;
        }

        // ensure drop exists for two-way
        if (selectedPlan?.tripType === 2) {
          const existingDrop = next[dayName].drop;

          // If today, drop cannot be before pickup
          const minDrop = dayjs(next[dayName].pickup, "HH:mm").isValid()
            ? next[dayName].pickup
            : "00:00";

          if (!existingDrop || existingDrop < minDrop) {
            next[dayName].drop = minDrop;
            changed = true;
          }
        }
      }
    });

    if (changed) {
      sessionStorage.setItem("dayTimeState", JSON.stringify(next));
      return next;
    }
    return prev;
  });
  }, [tempSelectedDays, startDate, selectedPlan, daySlot, setDayTimeState]);


  // change handler for parts
  const handleCustomTimeChange = useCallback(
    (dayName, type, part, value) => {
      setDayTimeState((prev) => {
        const currentDayData = prev[dayName] || { pickup: "00:00", drop: "00:00" };
        const currentTimeString = currentDayData[type] || "00:00";

        // parse existing time into 12h parts
        const [curH24Str, curMinStr] = currentTimeString.split(":");
        const curH24 = parseInt(curH24Str, 10);
        const curMin = curMinStr ?? "00";

        let curHour12 = 12;
        let curMinStr2 = curMin;
        let curPeriod = "AM";

        if (!Number.isNaN(curH24)) {
          if (curH24 === 0) {
            curHour12 = 12;
            curPeriod = "AM";
          } else if (curH24 === 12) {
            curHour12 = 12;
            curPeriod = "PM";
          } else if (curH24 > 12) {
            curHour12 = curH24 - 12;
            curPeriod = "PM";
          } else {
            curHour12 = curH24;
            curPeriod = "AM";
          }
        }

        let newHour12 = curHour12;
        let newMinute = curMinStr2;
        let newPeriod = curPeriod;

        if (part === "hour") newHour12 = parseInt(value, 10);
        else if (part === "minute") newMinute = value;
        else if (part === "period") newPeriod = value;

        const newHour24 = convertTo24Hour(newHour12, newPeriod);
        const safeHour24 = Number.isNaN(newHour24) ? 0 : newHour24;
        const minuteNum = parseInt(newMinute, 10);
        const safeMinute = Number.isNaN(minuteNum) ? 0 : minuteNum;

        const newTimeString = `${formatTimePart(safeHour24)}:${formatTimePart(safeMinute)}`;

        const updated = {
          ...prev,
          [dayName]: {
            ...currentDayData,
            [type]: newTimeString,
          },
        };

        // validation: drop must not equal pickup
        const newPickup = type === "pickup" ? newTimeString : currentDayData.pickup;
        const newDrop = type === "drop" ? newTimeString : currentDayData.drop;
        if (newPickup && newDrop && newPickup === newDrop) {
          setValidationErrors((errs) => ({ ...errs, [dayName]: "Drop time cannot be same as pickup time" }));
        } else {
          setValidationErrors((errs) => {
            const { [dayName]: removed, ...rest } = errs;
            return rest;
          });
        }

        sessionStorage.setItem("dayTimeState", JSON.stringify(updated));
        return updated;
      });
    },
    [convertTo24Hour, setDayTimeState]
  );

  // disable drop times earlier than pickup + 1 minute
  const isTimeBeforeMinTime = useCallback(
    (dayName, currentType, testHour12, testMinute, testPeriod) => {
      if (currentType !== "drop") return false;
      if (!testHour12 || testHour12 === "" || !testMinute || testMinute === "" || !testPeriod || testPeriod === "") return false;

      const pickup = dayTimeState[dayName]?.pickup;
      if (!pickup) return false;

      const [pH24, pMin] = pickup.split(":").map(Number);
      if (Number.isNaN(pH24) || Number.isNaN(pMin)) return false;

      const base = dayjs().startOf("day");
      const minAllowed = base.hour(pH24).minute(pMin).add(1, "minute");

      const tH24 = convertTo24Hour(testHour12, testPeriod);
      if (Number.isNaN(tH24)) return false;
      const testTime = base.hour(tH24).minute(parseInt(testMinute, 10));

      return testTime.isBefore(minAllowed, "minute");
    },
    [dayTimeState, convertTo24Hour]
  );

  // disable past options only when startDate is today AND this dayName is today
  const isBeforeNow = useCallback(
    (dayName, testHour12, testMinute, testPeriod) => {
      if (!testHour12 || testHour12 === "" || !testMinute || testMinute === "" || !testPeriod || testPeriod === "") return false;
      if (!startDate) return false;

      const todayStr = dayjs().format("YYYY-MM-DD");
      const startStr = dayjs(startDate).format("YYYY-MM-DD");
      if (todayStr !== startStr) return false;

      const todayName = dayjs().format("dddd").toLowerCase();
      if (!dayName || dayName.toLowerCase() !== todayName) return false;

      const base = dayjs();
      const testHour24 = convertTo24Hour(testHour12, testPeriod);
      if (Number.isNaN(testHour24)) return false;
      const testTime = dayjs().startOf("day").hour(testHour24).minute(parseInt(testMinute, 10));
      return testTime.isBefore(base, "minute");
    },
    [startDate, convertTo24Hour]
  );

  return (
    <>
      {tempSelectedDays?.map((dayIndex) => {
        const dayName = daySlot[dayIndex + 1];
        const pickupTimeStr = (dayTimeState[dayName] && dayTimeState[dayName].pickup) || "00:00";
        const dropTimeStr = (selectedPlan?.tripType === 2 && dayTimeState[dayName] && dayTimeState[dayName].drop) ? dayTimeState[dayName].drop : null;
        const hasError = !!validationErrors[dayName];

        // parse pickup to 12h UI parts (always present as "HH:mm")
        let initialPickupHour = 12;
        let initialPickupMinute = "00";
        let initialPickupPeriod = "AM";
        if (pickupTimeStr) {
          const [h24Str, mStr] = pickupTimeStr.split(":");
          const h24 = parseInt(h24Str, 10);
          const m = parseInt(mStr, 10);
          if (!Number.isNaN(h24) && !Number.isNaN(m)) {
            initialPickupMinute = formatTimePart(m);
            if (h24 === 0) {
              initialPickupHour = 12;
              initialPickupPeriod = "AM";
            } else if (h24 === 12) {
              initialPickupHour = 12;
              initialPickupPeriod = "PM";
            } else if (h24 > 12) {
              initialPickupHour = h24 - 12;
              initialPickupPeriod = "PM";
            } else {
              initialPickupHour = h24;
              initialPickupPeriod = "AM";
            }
          }
        }

        // parse drop
        let initialDropHour = 12;
        let initialDropMinute = "00";
        let initialDropPeriod = "AM";
        if (dropTimeStr) {
          const [h24Str, mStr] = dropTimeStr.split(":");
          const h24 = parseInt(h24Str, 10);
          const m = parseInt(mStr, 10);
          if (!Number.isNaN(h24) && !Number.isNaN(m)) {
            initialDropMinute = formatTimePart(m);
            if (h24 === 0) {
              initialDropHour = 12;
              initialDropPeriod = "AM";
            } else if (h24 === 12) {
              initialDropHour = 12;
              initialDropPeriod = "PM";
            } else if (h24 > 12) {
              initialDropHour = h24 - 12;
              initialDropPeriod = "PM";
            } else {
              initialDropHour = h24;
              initialDropPeriod = "AM";
            }
          }
        }

        return (
          <Box key={dayName} sx={{ flexGrow: 1, mt: 2 }}>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={4}>
                <Typography sx={{ fontSize: 14, pt: 1 }}>{dayName}</Typography>
              </Grid>

              <Grid item xs={8}>
                <Grid container spacing={2} direction="column">
                  {/* Pickup */}
                  <Grid item>
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                      One Way Pickup Time
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        label="Hour"
                        value={initialPickupHour}
                        onChange={(e) => handleCustomTimeChange(dayName, "pickup", "hour", e.target.value)}
                        sx={{ minWidth: 70 }}
                        size="small"
                      >
                        {hoursOptions.map((h) => (
                          <MenuItem
                            key={h}
                            value={h}
                            disabled={isBeforeNow(dayName, h, initialPickupMinute || "00", initialPickupPeriod || "AM")}
                          >
                            {h}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        label="Minute"
                        value={initialPickupMinute}
                        onChange={(e) => handleCustomTimeChange(dayName, "pickup", "minute", e.target.value)}
                        sx={{ minWidth: 70 }}
                        size="small"
                      >
                        {minutesOptions.map((m) => (
                          <MenuItem
                            key={m}
                            value={m}
                            disabled={isBeforeNow(dayName, initialPickupHour || 12, m, initialPickupPeriod || "AM")}
                          >
                            {m}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        label="AM/PM"
                        value={initialPickupPeriod}
                        onChange={(e) => handleCustomTimeChange(dayName, "pickup", "period", e.target.value)}
                        sx={{ minWidth: 80 }}
                        size="small"
                      >
                        {ampmOptions.map((p) => (
                          <MenuItem
                            key={p}
                            value={p}
                            disabled={isBeforeNow(dayName, initialPickupHour || 12, initialPickupMinute || "00", p)}
                          >
                            {p}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Grid>

                  {/* Drop */}
                  {selectedPlan?.tripType === 2 && (
                    <Grid item>
                      <Typography variant="caption" sx={{ mb: 0.5, mt: 1 }}>
                        Two Way Pickup Time
                      </Typography>

                      <Stack direction="row" spacing={1}>
                        <TextField
                          select
                          label="Hour"
                          value={initialDropHour}
                          onChange={(e) => handleCustomTimeChange(dayName, "drop", "hour", e.target.value)}
                          sx={{ minWidth: 70 }}
                          size="small"
                          error={hasError}
                        >
                          {hoursOptions.map((h) => (
                            <MenuItem
                              key={h}
                              value={h}
                              disabled={isTimeBeforeMinTime(dayName, "drop", h, initialDropMinute || "00", initialDropPeriod || "AM")}
                            >
                              {h}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Minute"
                          value={initialDropMinute}
                          onChange={(e) => handleCustomTimeChange(dayName, "drop", "minute", e.target.value)}
                          sx={{ minWidth: 70 }}
                          size="small"
                          error={hasError}
                        >
                          {minutesOptions.map((m) => (
                            <MenuItem
                              key={m}
                              value={m}
                              disabled={isTimeBeforeMinTime(dayName, "drop", initialDropHour || 12, m, initialDropPeriod || "AM")}
                            >
                              {m}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="AM/PM"
                          value={initialDropPeriod}
                          onChange={(e) => handleCustomTimeChange(dayName, "drop", "period", e.target.value)}
                          sx={{ minWidth: 80 }}
                          size="small"
                          error={hasError}
                        >
                          {ampmOptions.map((p) => (
                            <MenuItem
                              key={p}
                              value={p}
                              disabled={isTimeBeforeMinTime(dayName, "drop", initialDropHour || 12, initialDropMinute || "00", p)}
                            >
                              {p}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      {hasError && (
                        <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                          {validationErrors[dayName]}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </>
  );
};

export default React.memo(CustomerTimePicker);
